import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { getTenantCode } from '../../auth/authApi.js';
import { ApiError } from '../../api/client.js';
import { usePublicBranding, portalName } from '../../theme/usePublicBranding.js';
import { LoginShell } from '../../../../shared/auth/LoginShell.js';

export function LoginPage(): ReactNode {
  const { login } = useAuth();
  const nav = useNavigate();
  // `/v1/auth/login` rejects a request without X-Tenant-Code (EX-TEN-05), so
  // the tenant is asked for here exactly as on the admin sign-in screen.
  const [tenantCode, setTenantCode] = useState(getTenantCode());
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const branding = usePublicBranding(tenantCode);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!tenantCode.trim()) { setError('Tenant code is required'); return; }
    if (!username.trim()) { setError('Login id is required'); return; }
    setBusy(true);
    try {
      const res = await login(username.trim(), password, tenantCode.trim());
      if (res.requiresMfa) nav('/auth/mfa');
      else nav('/vendor/dashboard');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign in failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main aria-label="login">
      <LoginShell
        productName={portalName(branding)}
        title="Welcome back"
        subtitle="Sign in to your branding management workspace."
      >
        <form onSubmit={onSubmit} aria-label="login-form">
          <div className="login-field login-field--row">
            <div>
              <label htmlFor="v-tenant-code">Tenant code</label>
              <div className="login-control">
                <input id="v-tenant-code" value={tenantCode} onChange={(e) => setTenantCode(e.target.value)} aria-label="tenant-code" required />
              </div>
            </div>
            <div>
              <label htmlFor="v-username">Login id</label>
              <div className="login-control">
                <input id="v-username" value={username} onChange={(e) => setUsername(e.target.value)} aria-label="username" required />
              </div>
            </div>
          </div>

          <div className="login-field">
            <label htmlFor="v-password">Password</label>
            <div className="login-control login-control--with-toggle">
              <input id="v-password" value={password} onChange={(e) => setPassword(e.target.value)} type={showPassword ? 'text' : 'password'} aria-label="password" required />
              <button type="button" className="login-toggle" aria-pressed={showPassword} aria-controls="v-password" onClick={() => setShowPassword((v) => !v)}>
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          {error ? <div role="alert" className="login-alert">{error}</div> : null}

          <button type="submit" className="login-submit" disabled={busy}>
            <span>{busy ? 'Signing in…' : 'Sign in'}</span>
            <span aria-hidden="true">→</span>
          </button>
        </form>
      </LoginShell>
    </main>
  );
}
